/**
 * Instant variant gallery via image alt tags (#60 Capsules).
 * Runs on option click (before section fetch) so switching feels native.
 * Images without "#" stay visible for all variants.
 *
 * Matching is loose on purpose:
 *   #60 Capsules  #60Capsules  #60 Softgels
 * all match the selected option if the compact form aligns with either the
 * option value OR the visible button/label text (Shopify often stores
 * "30 Capsules" while the pill shows "30 Softgels").
 */
(function () {
  const HIDDEN_CLASS = 'spunge-variant-hidden';
  let lastFilterKey = '';

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  // "60 Capsules" / "60Capsules" / "60 Softgels!" → "60capsules" / "60softgels"
  function compact(value) {
    return normalize(value).replace(/[^a-z0-9]+/g, '');
  }

  function extractVariantTags(alt) {
    if (!alt || alt.indexOf('#') === -1) return [];
    const tags = [];
    const re = /#\s*([^#]+)/g;
    let match;
    while ((match = re.exec(alt)) !== null) {
      const raw = String(match[1] || '').trim();
      if (!raw) continue;
      tags.push({
        normalized: normalize(raw),
        compact: compact(raw),
      });
    }
    return tags;
  }

  function pushOptionTokens(values, input) {
    if (!input) return;
    if (input.value) values.push(normalize(input.value));

    // Visible pill text can differ from the stored option value.
    const label =
      (input.id && document.querySelector('label[for="' + input.id + '"]')) ||
      input.closest('label');
    if (label) {
      const clone = label.cloneNode(true);
      clone.querySelectorAll('.visually-hidden, .sold-out-message').forEach((n) => n.remove());
      const text = normalize(clone.textContent);
      if (text) values.push(text);
    }
  }

  function getSelectedOptionValues(root, predictedInput) {
    const scope = root || document;
    const values = [];

    scope.querySelectorAll('.product-form__input').forEach((group) => {
      if (predictedInput && group.contains(predictedInput)) {
        pushOptionTokens(values, predictedInput);
        return;
      }
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) {
        pushOptionTokens(values, checked);
        return;
      }
      const select = group.querySelector('select');
      if (select && select.value) {
        values.push(normalize(select.value));
        const opt = select.options[select.selectedIndex];
        if (opt && opt.text) values.push(normalize(opt.text));
      }
    });

    return values.filter(Boolean);
  }

  function getVariantFromJson() {
    const jsonEl = document.querySelector(
      'variant-selects [data-selected-variant], color-swatch [data-selected-variant], product-info [data-selected-variant]'
    );
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      return null;
    }
  }

  function mediaMatchesSelection(alt, selectedValues) {
    const tags = extractVariantTags(alt);
    if (!tags.length) return true;
    if (!selectedValues.length) return true;

    const selectedCompact = selectedValues.map(compact).filter(Boolean);
    return tags.some(
      (tag) =>
        selectedValues.some((value) => value === tag.normalized) ||
        selectedCompact.some((value) => value === tag.compact)
    );
  }

  // True when every node added or removed is a click-to-zoom overlay from
  // assets/magnify.js. Those land inside .product__media-list, so they trip the
  // gallery MutationObserver even though nothing about the variant changed.
  function isZoomOverlayOnly(mutations) {
    if (!mutations || !mutations.length) return false;

    return mutations.every((mutation) => {
      const nodes = [].concat(
        Array.prototype.slice.call(mutation.addedNodes || []),
        Array.prototype.slice.call(mutation.removedNodes || [])
      );
      if (!nodes.length) return false;
      return nodes.every(
        (node) =>
          node.nodeType === 1 &&
          node.classList &&
          node.classList.contains('image-magnify-full-size')
      );
    });
  }

  function getMediaAlt(el) {
    if (el.dataset && el.dataset.mediaAlt != null && el.dataset.mediaAlt !== '') {
      return el.dataset.mediaAlt;
    }
    const img = el.matches && el.matches('img') ? el : el.querySelector('img');
    return img ? img.getAttribute('alt') || '' : '';
  }

  function preloadGalleryImages(gallery) {
    gallery.querySelectorAll('.product__media-item img').forEach((img) => {
      img.loading = 'eager';
      if (img.decode) img.decode().catch(() => {});
      // Warm CDN cache for current src
      if (img.currentSrc || img.src) {
        const warm = new Image();
        warm.src = img.currentSrc || img.src;
      }
    });
  }

  function activateMediaFast(gallery, mediaId) {
    if (!mediaId) return;

    const slides = gallery.querySelectorAll('.product__media-item[data-media-id]');
    let activeMedia = null;

    slides.forEach((slide) => {
      const isActive = slide.dataset.mediaId === mediaId;
      slide.classList.toggle('is-active', isActive);
      if (isActive) activeMedia = slide;
    });

    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbs) {
      thumbs.querySelectorAll('button').forEach((btn) => btn.removeAttribute('aria-current'));
      const activeThumb = thumbs.querySelector(`[data-target="${mediaId}"]`);
      if (activeThumb) {
        const button = activeThumb.querySelector('button');
        if (button) button.setAttribute('aria-current', 'true');
      }
    }

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (viewer) viewer.dataset.selectedMediaId = mediaId;

    // Horizontal slide only — skip page scroll used by setActiveMedia
    if (activeMedia && activeMedia.parentElement) {
      activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
    }
  }

  function filterByOptions(selectedValues, options) {
    const opts = options || {};
    const key = selectedValues.join('|');
    if (!opts.force && key && key === lastFilterKey) return;
    lastFilterKey = key;

    document.querySelectorAll('media-gallery').forEach((gallery) => {
      if (!opts.skipPreload) preloadGalleryImages(gallery);

      const slides = gallery.querySelectorAll('.product__media-item[data-media-id]');
      const thumbs = gallery.querySelectorAll('.thumbnail-list__item[data-target]');
      let firstVisibleId = null;
      let firstTaggedId = null;
      let visibleCount = 0;

      slides.forEach((slide) => {
        const alt = getMediaAlt(slide);
        const show = mediaMatchesSelection(alt, selectedValues);
        slide.classList.toggle(HIDDEN_CLASS, !show);
        // Clear any inline display from older scripts
        if (show) slide.style.removeProperty('display');
        else slide.style.display = 'none';

        if (show) {
          visibleCount += 1;
          if (!firstVisibleId) firstVisibleId = slide.dataset.mediaId;
          if (!firstTaggedId && extractVariantTags(alt).length) {
            firstTaggedId = slide.dataset.mediaId;
          }
        }
      });

      thumbs.forEach((thumb) => {
        const show = mediaMatchesSelection(getMediaAlt(thumb), selectedValues);
        thumb.classList.toggle(HIDDEN_CLASS, !show);
        if (show) thumb.style.removeProperty('display');
        else thumb.style.display = 'none';
      });

      const counterTotal = gallery.querySelector('.slider-counter--total');
      if (counterTotal && visibleCount > 0) counterTotal.textContent = String(visibleCount);

      const active = gallery.querySelector('.product__media-item.is-active');
      const activeStillVisible = active && !active.classList.contains(HIDDEN_CLASS);
      const activateId = firstTaggedId || (activeStillVisible ? active.dataset.mediaId : firstVisibleId);
      activateMediaFast(gallery, activateId);
    });

    document.querySelectorAll('product-modal .product-media-modal__content [data-media-id]').forEach((el) => {
      const show = mediaMatchesSelection(getMediaAlt(el), selectedValues);
      el.classList.toggle(HIDDEN_CLASS, !show);
      if (show) el.style.removeProperty('display');
      else el.style.display = 'none';
    });
  }

  function filterFromRoot(root, predictedInput, options) {
    const selectedValues = getSelectedOptionValues(root, predictedInput);
    if (!selectedValues.length) {
      const variant = getVariantFromJson();
      if (variant && Array.isArray(variant.options)) {
        filterByOptions(variant.options.map(normalize), options);
      }
      return;
    }
    filterByOptions(selectedValues, options);
  }

  function bindProductInfo(productInfo) {
    if (!productInfo || productInfo.dataset.spungeVariantGallery === '1') return;
    productInfo.dataset.spungeVariantGallery = '1';

    filterFromRoot(productInfo, null, { force: true });

    // Predict selection on pointerdown for zero-latency feel (before change/fetch)
    productInfo.addEventListener(
      'pointerdown',
      (event) => {
        const input = event.target.closest('.product-form__input input[type="radio"]');
        if (!input || input.disabled) return;
        filterFromRoot(productInfo, input, { force: true, skipPreload: true });
      },
      true
    );

    productInfo.addEventListener('change', (event) => {
      if (!event.target.closest('.product-form__input')) return;
      filterFromRoot(productInfo, null, { force: true, skipPreload: true });
    });

    const mediaList = productInfo.querySelector('.product__media-list');
    if (mediaList && window.MutationObserver) {
      let scheduled = false;
      new MutationObserver((mutations) => {
        // The click-to-zoom overlay (assets/magnify.js) is inserted as a child
        // of the image it magnifies, which lives inside this list -- so opening
        // or closing the zoom fires this observer. Re-running the filter from
        // here calls activateMediaFast(firstTaggedId || ...), which yanks the
        // gallery back to the first tagged image: click zoom on the 4th photo
        // and you land on the 1st. Those mutations are ours, not a variant
        // change, so skip them.
        if (isZoomOverlayOnly(mutations)) return;

        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          lastFilterKey = '';
          filterFromRoot(productInfo, null, { force: true, skipPreload: true });
        });
      }).observe(mediaList, { childList: true, subtree: true });
    }
  }

  function boot() {
    document.querySelectorAll('product-info').forEach(bindProductInfo);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
        const target = event?.data?.event?.target;
        const root = target?.closest?.('product-info');
        if (root) filterFromRoot(root, null, { force: true, skipPreload: true });
      });

      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        if (variant?.options) {
          filterByOptions(variant.options.map(normalize), { force: true, skipPreload: true });
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
